import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

// Types
export interface UserItem {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'driver' | 'workshop';
  active: boolean;
  phone: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UsersListResponse {
  data: UserItem[];
  meta: { page: number; limit: number; total: number };
}

export interface InvitationItem {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  acceptedAt: string | null;
  status: 'pending' | 'accepted' | 'expired';
  createdAt: string;
}

// Users hooks
const KEY = {
  users: (params: Record<string, unknown>) => ['users', params] as const,
  invitations: ['invitations'] as const,
};

export function useUsersList(params: { page?: number; limit?: number; q?: string; role?: string; active?: string } = {}) {
  return useQuery<UsersListResponse>({
    queryKey: KEY.users(params),
    queryFn: async () => {
      const { data } = await api.get<UsersListResponse>('/users', { params });
      return data;
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; name?: string; phone?: string; role?: string; active?: boolean }) => {
      const { data } = await api.patch<{ data: UserItem }>(`/users/${id}`, body);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/users/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useAdminResetPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, newPassword }: { id: string; newPassword: string }) => {
      const { data } = await api.post<{ data: { id: string; email: string } }>(
        `/users/${id}/reset-password`,
        { newPassword },
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

// Invitations hooks
export function useInvitationsList() {
  return useQuery<{ data: InvitationItem[] }>({
    queryKey: KEY.invitations,
    queryFn: async () => {
      const { data } = await api.get<{ data: InvitationItem[] }>('/invitations');
      return data;
    },
  });
}

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; role: string; message?: string }) => {
      const { data } = await api.post<{ data: InvitationItem }>('/invitations', input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.invitations }),
  });
}

export function useCancelInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/invitations/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.invitations }),
  });
}

// Password
export async function apiChangePassword(currentPassword: string, newPassword: string) {
  await api.post('/auth/change-password', { currentPassword, newPassword });
}

export async function apiForgotPassword(email: string) {
  await api.post('/auth/forgot-password', { email });
}

export async function apiResetPassword(token: string, newPassword: string) {
  await api.post('/auth/reset-password', { token, newPassword });
}

export async function apiAcceptInvitation(input: { token: string; name: string; password: string; phone?: string }) {
  const { data } = await api.post<{ data: { message: string; email: string } }>('/invitations/accept', input);
  return data.data;
}
